/**
 * API client for admin CRUD of per-tenant phrase guidelines
 * (positive/negative phrases appended to the LLM prompt during transcript
 * annotation).
 *
 * Backend: routes/admin_phrase_rules.py.
 */

import { fetchApi } from '../utils/api';
import { authHeader } from './authToken';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

export type Polarity = 'positive' | 'negative';

export interface PhraseRule {
  id: string;
  tenant_id: string;
  phrase: string;
  polarity: Polarity;
  light_bulb_text: string;
  is_active: boolean;
  created: string | null;
  updated: string | null;
}

export interface PhraseRulesEnvelope {
  tenant_id: string;
  rules: PhraseRule[];
}

export async function listPhraseRules(tenantId: string): Promise<PhraseRulesEnvelope> {
  return fetchApi<PhraseRulesEnvelope>(`/api/tenant/${tenantId}/phrase-rules`, {
    headers: getAuthHeaders(),
  });
}

export async function createPhraseRule(
  tenantId: string,
  data: {
    phrase: string;
    polarity: Polarity;
    light_bulb_text: string;
    is_active?: boolean;
  }
): Promise<{ id: string }> {
  return fetchApi(`/api/tenant/${tenantId}/phrase-rules`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function updatePhraseRule(
  ruleId: string,
  data: Partial<{
    phrase: string;
    polarity: Polarity;
    light_bulb_text: string;
    is_active: boolean;
  }>
): Promise<void> {
  await fetchApi(`/api/phrase-rules/${ruleId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: data,
  });
}

export async function deletePhraseRule(ruleId: string): Promise<void> {
  await fetchApi(`/api/phrase-rules/${ruleId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
}
